import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './MarketingLandingPage.css';

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <img src="/favicon.ico" alt="Logo" className="navbar-logo" />
        <span className="navbar-title">Ordease</span>
      </div>
      <div className="menu-icon" onClick={toggleMenu}>
        <div className={isOpen ? "bar bar1 open" : "bar bar1"}></div>
        <div className={isOpen ? "bar bar2 open" : "bar bar2"}></div>
        <div className={isOpen ? "bar bar3 open" : "bar bar3"}></div>
      </div>
      <div className={`navbar-center ${isOpen ? 'open' : ''}`}>
        <a href="#features" className="navbar-link">Features</a>
        <a href="#pricing" className="navbar-link">Pricing</a>
        <a href="#contact" className="navbar-link">Contact Us</a>
      </div>
      <div className={`navbar-right ${isOpen ? 'open' : ''}`}>
        <Link to="/login" className="navbar-btn">Log in</Link>
        <Link to="/landing" className="navbar-btn navbar-btn-primary">Get Started</Link>
      </div>
    </nav>
  );
}

function MarketingLandingPage() {
  const [showDemo, setShowDemo] = useState(false);
  const [showProductVideo, setShowProductVideo] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month');

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('show');
        }
      });
    });

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="marketing-landing">
      <Navbar />
      {/* Hero Section */}
      <motion.section 
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-content">
          <div className="hero-text">
            <h1>Manage Orders with Ease and Clarity</h1>
            <p className="hero-subtitle">
              From approval to dispatch — stay in sync, in control, and on time.
            </p>
            <div className="cta-buttons">
              <div className="cta-buttons-row">
                <Link to="/landing" className="cta-primary">
                  Get Started
                </Link>
                <a 
                  href="https://calendly.com/ordease"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-tertiary"
                >
                  <span className="calendar-icon">📅</span>
                  Book Live Demo
                </a>
              </div>
            </div>
          </div>
          <motion.div 
            className="hero-video"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <div className="product-video">
              <img 
                src="/images/landing/dashboard-preview.png" 
                alt="Order Management Dashboard Preview" 
                className="video-thumbnail"
              />
              <div className="video-overlay">
                <button 
                  className="play-button"
                  onClick={() => setShowProductVideo(true)}
                >
                  <span className="play-icon">▶</span>
                  <span className="play-text">Watch Product Demo</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* WhatsApp vs Ordease Comparison Section */}
      <section className="whatsapp-comparison-section">
        <div className="comparison-content">
          <motion.div 
            className="comparison-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="comparison-title">Still managing orders through chats?</h2>
            <p className="comparison-subtitle-new">
              Like WhatsApp, Telegram, etc.
            </p>
            <p className="comparison-subtitle">
              It may work for chats, but not for business. Here's why WhatsApp slows your team down — and how Ordease fixes it.
            </p>
          </motion.div>

          <motion.div 
            className="comparison-split"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {/* WhatsApp Side */}
            <div className="whatsapp-side">
              <div className="side-label">Before</div>
              <div className="whatsapp-header">
                <div className="whatsapp-icon">💬</div>
                <h3>WhatsApp Chaos</h3>
              </div>
              <div className="problem-points">
                <div className="problem-point">
                  <span className="point-icon">📂</span>
                  <span>Scattered files, lost in endless chats</span>
                </div>
                <div className="problem-point">
                  <span className="point-icon">📞</span>
                  <span>Constant follow-ups from sales & inventory staff</span>
                </div>
                <div className="problem-point">
                  <span className="point-icon">❌</span>
                  <span>No clear order status, approvals, or accountability</span>
              </div>
                <div className="problem-point">
                  <span className="point-icon">⏳</span>
                  <span>Wasted hours scrolling through messages</span>
              </div>
              </div>
            </div>

            {/* Ordease Side */}
            <div className="ordease-side">
              <div className="side-label">After</div>
              <div className="ordease-header">
                <div className="ordease-icon">🚀</div>
                <h3>Ordease Clarity</h3>
              </div>
              <div className="solution-points">
                <div className="solution-point">
                  <span className="point-icon">✅</span>
                  <span>One place for every order & file</span>
              </div>
                <div className="solution-point">
                  <span className="point-icon">✅</span>
                  <span>Real-time updates for each stage</span>
              </div>
                <div className="solution-point">
                  <span className="point-icon">✅</span>
                  <span>Clear accountability & order history</span>
                </div>
                <div className="solution-point">
                  <span className="point-icon">✅</span>
                  <span>Smooth collaboration across teams</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div 
            className="comparison-cta"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Link to="/landing" className="cta-primary comparison-cta-button">
              👉 Ditch WhatsApp Chaos — Switch to Ordease
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Target Audience Section - Moved to position before Features */}
      <section className="audience-section">
        <div className="audience-content">
          {/* Section Title */}
          <motion.h2 
            className="audience-title reveal"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Is Ordease Right for You?
          </motion.h2>
          
          <motion.p 
            className="audience-subtitle reveal"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            The Smarter way to run your Team and Operations
          </motion.p>

          {/* Target Users */}
          <motion.div 
            className="target-users"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="user-cards">
              <div className="user-card">
                <div className="user-icon">📞</div>
                <h3>Sales Team</h3>
                <p>Instant updates and complete access to order files to share with customers</p>
              </div>
              <div className="user-card">
                <div className="user-icon">📦</div>
                <h3>Inventory Manager</h3>
                <p>Track pending orders with clear status updates</p>
              </div>
              <div className="user-card">
                <div className="user-icon">🧾</div>
                <h3>Accountant</h3>
                <p>Clear records of bills, challans, delivery recipt at one place</p>
              </div>
              <div className="user-card">
                <div className="user-icon">👨🏽‍💼</div>
                <h3>Business Owner</h3>
                <p>Stay on top of every order from start to end</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <h2 className="section-title reveal">Powerful Features for Modern Business</h2>
        
        <div className="feature-row">
          <motion.div 
            className="feature-content"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="feature-icon">📦</div>
            <h3>Real-time Order Tracking</h3>
            <p>Monitor every order's journey from creation to delivery with our intuitive dashboard</p>
          </motion.div>
          <motion.div 
            className="feature-image"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img 
              src="/images/landing/tracking-preview.png" 
              alt="Order Tracking" 
            />
          </motion.div>
        </div>

        <div className="feature-row reverse">
          <motion.div 
            className="feature-content"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="feature-icon">👥</div>
            <h3>Role-based Workflows</h3>
            <p>Customized interfaces for Sales, Inventory, Accounting, and Management teams</p>
          </motion.div>
          <motion.div 
            className="feature-image"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img 
              src="/images/landing/workflow-preview.png" 
              alt="Role-based Workflows" 
            />
          </motion.div>
        </div>

        <div className="feature-row">
          <motion.div 
            className="feature-content"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="feature-icon">📊</div>
            <h3>Business Analytics</h3>
            <p>Learn about your business with our analytics tools providing insights into your operations</p>
          </motion.div>
          <motion.div 
            className="feature-image"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img 
              src="/images/landing/billing-preview.png" 
              alt="Automated Billing" 
            />
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing">
        <div className="pricing-content">
          <motion.h2 
            className="section-title reveal"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Simple, Transparent Pricing
          </motion.h2>
          
          <motion.p 
            className="pricing-subtitle reveal"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Choose the perfect plan for your business. Start with a 30-day free trial.
          </motion.p>

          {/* Pricing Toggle */}
          <motion.div 
            className="pricing-toggle-container"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="pricing-toggle">
              <button 
                className={`pricing-toggle-option ${billingInterval === 'month' ? 'active' : ''}`}
                onClick={() => setBillingInterval('month')}
              >
                Monthly
              </button>
              <button 
                className={`pricing-toggle-option ${billingInterval === 'year' ? 'active' : ''}`}
                onClick={() => setBillingInterval('year')}
              >
                Yearly
                <span className="pricing-savings-badge">Save up to 17%</span>
              </button>
            </div>
          </motion.div>

          {/* Pricing Cards */}
          <motion.div 
            className="pricing-cards"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            {/* Basic Plan */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <h3 className="pricing-plan-name">Basic</h3>
                <p className="pricing-plan-description">Perfect for small teams getting started</p>
              </div>
              <div className="pricing-card-price">
                <span className="pricing-amount">
                  {billingInterval === 'month' ? '₹999' : '₹9,999'}
                </span>
                <span className="pricing-period">
                  {billingInterval === 'month' ? 'per month' : 'per year'}
                </span>
              </div>
              <div className="pricing-card-limits">
                <div className="pricing-limit">
                  <span className="pricing-limit-value">6</span>
                  <span className="pricing-limit-label">Users</span>
                </div>
                <div className="pricing-limit">
                  <span className="pricing-limit-value">80</span>
                  <span className="pricing-limit-label">Orders</span>
                </div>
                <div className="pricing-limit">
                  <span className="pricing-limit-value">30</span>
                  <span className="pricing-limit-label">Days Backup</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>Order Management System</li>
                <li>Real-time Order Updates</li>
                <li>Mobile App Access</li>
              </ul>
              <Link to="/landing" className="pricing-button">
                Start Free Trial
              </Link>
            </div>

            {/* Premium Plan - Most Popular */}
            <div className="pricing-card popular">
              <div className="pricing-badge">Most Popular</div>
              <div className="pricing-card-header">
                <h3 className="pricing-plan-name">Premium</h3>
                <p className="pricing-plan-description">Ideal for growing businesses</p>
              </div>
              <div className="pricing-card-price">
                <span className="pricing-amount">
                  {billingInterval === 'month' ? '₹1,499' : '₹14,999'}
                </span>
                <span className="pricing-period">
                  {billingInterval === 'month' ? 'per month' : 'per year'}
                </span>
              </div>
              <div className="pricing-card-limits">
                <div className="pricing-limit">
                  <span className="pricing-limit-value">12</span>
                  <span className="pricing-limit-label">Users</span>
                </div>
                <div className="pricing-limit">
                  <span className="pricing-limit-value">200</span>
                  <span className="pricing-limit-label">Orders</span>
                </div>
                <div className="pricing-limit">
                  <span className="pricing-limit-value">65</span>
                  <span className="pricing-limit-label">Days Backup</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>Everything in Basic</li>
                <li>Priority Customer Support</li>
                <li>Advanced Analytics & Reports</li>
              </ul>
              <Link to="/landing" className="pricing-button primary">
                Start Free Trial
              </Link>
            </div>

            {/* Premium Plus Plan */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <h3 className="pricing-plan-name">Premium+</h3>
                <p className="pricing-plan-description">For established businesses with high volume</p>
              </div>
              <div className="pricing-card-price">
                <span className="pricing-amount">
                  {billingInterval === 'month' ? '₹2,199' : '₹21,999'}
                </span>
                <span className="pricing-period">
                  {billingInterval === 'month' ? 'per month' : 'per year'}
                </span>
              </div>
              <div className="pricing-card-limits">
                <div className="pricing-limit">
                  <span className="pricing-limit-value">20</span>
                  <span className="pricing-limit-label">Users</span>
                </div>
                <div className="pricing-limit">
                  <span className="pricing-limit-value">400</span>
                  <span className="pricing-limit-label">Orders</span>
                </div>
                <div className="pricing-limit">
                  <span className="pricing-limit-value">90</span>
                  <span className="pricing-limit-label">Days Backup</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>Everything in Premium</li>
                <li>Advanced User Management</li>
                <li>Dedicated Account Manager</li>
              </ul>
              <Link to="/landing" className="pricing-button">
                Start Free Trial
              </Link>
            </div>

            {/* Enterprise Plan */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <h3 className="pricing-plan-name">Enterprise</h3>
                <p className="pricing-plan-description">Unlimited scale for large organizations</p>
              </div>
              <div className="pricing-card-price">
                <span className="pricing-amount">
                  {billingInterval === 'month' ? '₹3,199' : '₹35,199'}
                </span>
                <span className="pricing-period">
                  {billingInterval === 'month' ? 'per month' : 'per year'}
                </span>
              </div>
              <div className="pricing-card-limits">
                <div className="pricing-limit">
                  <span className="pricing-limit-value">∞</span>
                  <span className="pricing-limit-label">Users</span>
                </div>
                <div className="pricing-limit">
                  <span className="pricing-limit-value">∞</span>
                  <span className="pricing-limit-label">Orders</span>
                </div>
                <div className="pricing-limit">
                  <span className="pricing-limit-value">∞</span>
                  <span className="pricing-limit-label">Forever Backup</span>
                </div>
              </div>
              <ul className="pricing-features">
                <li>Everything in Premium Plus</li>
                <li>Unlimited Users</li>
                <li>Unlimited Orders</li>
                <li>Forever Data Backup</li>
                <li>24/7 Priority Support</li>
              </ul>
              <Link to="/landing" className="pricing-button">
                Start Free Trial
              </Link>
            </div>
          </motion.div>

          {/* Additional Info */}
          <motion.div 
            className="pricing-footer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="pricing-footer-text">
              All plans include 24/7 support
            </p>
            <p className="pricing-footer-note">
              Additional users can be added for ₹50 per user (one-time charge)
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section 
        className="cta-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="cta-content">
          <h2>Ready to simplify your order management?</h2>
          <p>Join thousands already using our platform</p>
          <div className="cta-section-buttons">
            <Link to="/landing" className="cta-primary">
              Start Free Trial
            </Link>
            <a 
              href="https://calendly.com/ordease"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-secondary"
            >
              <span className="calendar-icon">📅</span>
              Schedule Demo Call
            </a>
          </div>
        </div>
      </motion.section>

      {/* Contact Us Section */}
      <section className="contact-section" id="contact">
        <div className="contact-content">
          <h2 className="section-title reveal">Contact Us</h2>
          <div className="contact-details">
            <p>Have questions? Get in touch with our team.</p>
            <p>
              Email: <a href="mailto:getordease@gmail.com">getordease@gmail.com</a>
            </p>
            <p>
              Phone: <a href="tel:+11234567890">+91 9971162351</a>
            </p>
          </div>
        </div>
      </section>

      {/* Demo Video Modal */}
      <AnimatePresence>
        {showDemo && (
          <motion.div 
            className="demo-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDemo(false)}
          >
            <motion.div 
              className="demo-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <button 
                className="close-button"
                onClick={() => setShowDemo(false)}
              >
                ×
              </button>
              <div className="video-container">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/AZIdxqN7TBI?rel=0&modestbranding=1&showinfo=0"
                  title="Product Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                ></iframe>
                
                {/* Fallback link in case embedding is disabled */}
                <div className="video-fallback">
                  <p>Can't see the video? <a href="https://youtu.be/AZIdxqN7TBI" target="_blank" rel="noopener noreferrer">Watch it on YouTube</a></p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Product Video Modal */}
        <AnimatePresence>
          {showProductVideo && (
            <motion.div 
              className="demo-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowProductVideo(false)}
            >
              <motion.div 
                className="demo-modal"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <button 
                  className="close-button"
                  onClick={() => setShowProductVideo(false)}
                >
                  ×
                </button>
                <div className="video-container">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/pbJ6VP07x4o?rel=0&modestbranding=1&showinfo=0"
                    title="Product Demo"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  ></iframe>
                  
                  {/* Fallback link in case embedding is disabled */}
                  <div className="video-fallback">
                    <p>Can't see the video? <a href="https://www.youtube.com/watch?v=pbJ6VP07x4o" target="_blank" rel="noopener noreferrer">Watch it on YouTube</a></p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </AnimatePresence>
    </div>
  );
}

export default MarketingLandingPage; 